import { call, put, select, takeLatest } from 'redux-saga/effects';

import { user_logout } from '../API/util';
import { actions as accountActions, selectors as accountSelectors, types as accountTypes } from '../Ducks/account';

import { types as opportunityTypes } from '../Ducks/opportunity';

import { callApi } from './utils';
import * as api from '../API/account';


function* login(action) {
    const { success, failure } = accountActions.saga.login;

    function* postApiSuccessCallEffect() {
        // Register timeout
        if (user_logout.timer) {
            window.clearTimeout(user_logout.timer);
            user_logout.timer = null;
        }

        user_logout.timer = setTimeout(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user_login_timestamp');
            if (!localStorage.getItem('token')) {
                window.location.href = '/'
            }
        }, user_logout.time * 60 * 60 * 1000);
    }

    yield callApi(
        call(api.login, action.data),
        success,
        failure,
        { postApiSuccessCallEffect }
    )
}

function* loginAs(action) {
    const { success, failure } = accountActions.saga.loginAs;

    function* postApiSuccessCallEffect() {
        let d = new Date();
        let YearMonth = d.getFullYear(d) + '-' + ('0' + (d.getMonth() + 1)).slice(-2);

        yield put({
            type: opportunityTypes.clearEvents
        });
        const cronofyAuthorized = yield select(accountSelectors.cronofyAuthorized);

        if (cronofyAuthorized)
            yield put({
                type: opportunityTypes.getAllEvents
            });

        yield put({
            type: opportunityTypes.getCalendar,
            data: YearMonth
        });
        action.data.navigateTo();
    }

    yield callApi(
        call(api.loginAs, action.data),
        success,
        failure,
        { postApiSuccessCallEffect }
    )
}

function* loginAlias(action) {
    const { success, failure } = accountActions.saga.loginAlias;

    function* postApiSuccessCallEffect() {
        let d = new Date();
        let YearMonth = d.getFullYear(d) + '-' + ('0' + (d.getMonth() + 1)).slice(-2);

        yield put({
            type: opportunityTypes.clearEvents
        });
        const cronofyAuthorized = yield select(accountSelectors.cronofyAuthorized);

        if (cronofyAuthorized)
            yield put({
                type: opportunityTypes.getAllEvents
            });

        yield put({
            type: opportunityTypes.getCalendar,
            data: YearMonth
        });
        action.data.navigateTo();
    }

    yield callApi(
        call(api.loginAlias, action.data),
        success,
        failure,
        { postApiSuccessCallEffect }
    )
}

function* setParentUser(action) {
    const { success, failure } = accountActions.saga.setParentUser;

    yield callApi(
        call(api.setParentUser, action.data),
        success,
        failure
    )
}

function* azureLogin(action) {
    const { success, failure } = accountActions.saga.azureLogin;

    const failureHandler = (error) => {
        // Clear storage to prevent auto-login loops provided by MSAL/cache
        sessionStorage.clear();
        localStorage.clear();
        return failure(error);
    }

    yield callApi(
        call(api.azureLogin, action.data),
        success,
        failureHandler
    )
}

function* linkedinLogin(action) {
    const { success, failure } = accountActions.saga.linkedinLogin;

    const LINKEDIN_FAILED_ATTEMPTS_KEY = 'linkedin_failed_attempts';
    const MAX_FAILED_ATTEMPTS = 3;

    function handleError(error) {
        // Get current failed attempt count
        let failedAttempts = parseInt(sessionStorage.getItem(LINKEDIN_FAILED_ATTEMPTS_KEY) || '0');
        failedAttempts++;
        sessionStorage.setItem(LINKEDIN_FAILED_ATTEMPTS_KEY, failedAttempts.toString());

        let errorMessage = 'LinkedIn login failed. Unable to recognize you as Network Director.';

        // Determine error type and customize message
        if (error) {
            const status = error.status || error.error_code;

            if (status == 404) {
                errorMessage = 'No User/Network Director found with this account';
                // Don't count distinct user not found as a failed password attempt
                failedAttempts--;
                sessionStorage.setItem(LINKEDIN_FAILED_ATTEMPTS_KEY, failedAttempts.toString());
            } else if (status === 401 || status === 403) {
                // Authentication error
                errorMessage = 'LinkedIn login failed. Please check that you use your Linkedin login and password and try again.';

                if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
                    errorMessage += ' If you continue to experience issues, please reset your password.';
                }
            } else if (status >= 500 || error.message?.includes('network') || error.message?.includes('timeout')) {
                // Network/connectivity error
                errorMessage = 'LinkedIn login failed due to network issues. Please try again later.';

                // Don't count network errors toward failed attempts
                failedAttempts--;
                sessionStorage.setItem(LINKEDIN_FAILED_ATTEMPTS_KEY, failedAttempts.toString());
            } else if (error.message) {
                // Generic error with message
                if (error.message.includes('Network') || error.message.includes('timeout')) {
                    errorMessage = 'LinkedIn login failed due to network issues. Please try again later.';
                }
            } else if (error.error && typeof error.error === 'string') {
                // Fallback for when backend returns { error: "message" }
                errorMessage = error.error;
            }
        }

        // Return custom error with message
        return {
            ...error,
            message: errorMessage
        };
    }

    try {
        yield callApi(
            call(api.linkedinLogin, action.data),
            (data) => {
                const userDetails = data?.user?.user_details;

                // Validate Network Director via role_id or role_title
                const isNetworkDirector =
                    userDetails?.role_id == 8 ||
                    userDetails?.role_title === 'Network Director';

                if (!isNetworkDirector) {
                    // Reject login if user lacks required role
                    return failure({
                        message: 'LinkedIn login failed. Unable to recognize you as Network Director.'
                    });
                }
                return success(data);
            },
            (error) => {
                const customError = handleError(error);
                return failure(customError);
            }
        );

        // Clear failed attempts on successful login
        sessionStorage.removeItem(LINKEDIN_FAILED_ATTEMPTS_KEY);
    } catch (error) {
        // This catch block shouldn't be reached ideally as callApi handles it, but just in case
        const customError = handleError(error);
        yield put(failure(customError));
    }
}

function* forgotPassword(action) {
    const { success, failure } = accountActions.saga.forgotPassword;

    yield callApi(
        call(api.forgotPassword, action.data),
        success,
        failure
    )
}

function* logout(action) {
    const { success, failure } = accountActions.saga.logout;

    function* postApiSuccessCallEffect(res) {
        if (user_logout.timer) {
            window.clearTimeout(user_logout.timer);
            user_logout.timer = null;
        }
    }

    yield callApi(
        call(api.logout, action.data),
        success,
        failure,
        { postApiSuccessCallEffect }
    )
}

function* createNewPassword(action) {
    const { success, failure } = accountActions.saga.createNewPassword;
    yield callApi(call(api.createNewPassword, action.data),
        success,
        failure
    )
}

export default function* rootCustomerSaga() {
    yield takeLatest(accountTypes.login, login)
    yield takeLatest(accountTypes.loginAs, loginAs)
    yield takeLatest(accountTypes.loginAlias, loginAlias)
    yield takeLatest(accountTypes.azureLogin, azureLogin)
    yield takeLatest(accountTypes.linkedinLogin, linkedinLogin)
    yield takeLatest(accountTypes.forgotPassword, forgotPassword)
    yield takeLatest(accountTypes.setParentUser, setParentUser)
    yield takeLatest(accountTypes.logout, logout)
    yield takeLatest(accountTypes.createNewPassword, createNewPassword)
}